/**
 * @fileoverview Versions information for all questionnaires
 * 
 * This file contains version history and current version information for all questionnaires in the system.
 * Follow semantic versioning:
 * - MAJOR: Incompatible changes (new question structure, removed questions)
 * - MINOR: Added functionality in a backward compatible way (new questions)
 * - PATCH: Backward compatible fixes (wording changes, typo corrections)
 */

/**
 * Questionnaire types available in the system
 */
export enum QuestionnaireType {
  ESSENTIAL_EIGHT = 'essential-eight',
  // Add more questionnaire types as they are implemented
  // GDPR = 'gdpr',
  // ISO27001 = 'iso27001',
  // SOC2 = 'soc2'
}

/**
 * Interface for questionnaire version information
 */
export interface QuestionnaireVersion {
  /**
   * Semantic version number (MAJOR.MINOR.PATCH)
   */
  version: string;
  
  /**
   * ISO date string when this version was released
   */
  releaseDate: string;
  
  /**
   * Description of what changed in this version
   */
  description: string;
}

/**
 * Interface for versioned questionnaire
 */
export interface VersionedQuestionnaire {
  /**
   * Type identifier for the questionnaire
   */
  type: QuestionnaireType;

  /**
   * Version history for this questionnaire
   */
  versions: QuestionnaireVersion[];

  /**
   * Current active version (latest in the versions array)
   */
  currentVersion: QuestionnaireVersion;
}

/**
 * History of Essential Eight questionnaire versions
 */
export const ESSENTIAL_EIGHT_VERSIONS: QuestionnaireVersion[] = [
  {
    version: '1.0.0',
    releaseDate: '2025-09-01T00:00:00.000Z', // Current date as initial release
    description: 'Initial version of Essential Eight questionnaire with 8 categories and 3 maturity levels'
  }
  // When updating the questionnaire, add new versions here
  // Example:
  // {
  //   version: '1.1.0',
  //   releaseDate: '2025-10-15T00:00:00.000Z',
  //   description: 'Updated wording in Application Control questions for clarity'
  // }
];

/**
 * Current version of the Essential Eight questionnaire
 * Always reference the latest version in the versions array
 */
export const CURRENT_E8_VERSION = ESSENTIAL_EIGHT_VERSIONS[ESSENTIAL_EIGHT_VERSIONS.length - 1];

/**
 * Collection of all questionnaires with their version information
 */
export const QUESTIONNAIRES: Record<QuestionnaireType, VersionedQuestionnaire> = {
  [QuestionnaireType.ESSENTIAL_EIGHT]: {
    type: QuestionnaireType.ESSENTIAL_EIGHT,
    versions: ESSENTIAL_EIGHT_VERSIONS,
    currentVersion: CURRENT_E8_VERSION
  }
  // Add more questionnaires as they become available
};

/**
 * Helper function to get current version for a questionnaire type
 */
export function getCurrentVersionForQuestionnaire(type: QuestionnaireType): QuestionnaireVersion {
  return QUESTIONNAIRES[type].currentVersion;
}
